import React, { useRef, useState, useCallback, useEffect, useImperativeHandle } from 'react';
import type {
  ApprovalPanelProps,
  ApprovalPanelAPI,
  ApprovalHistoryItem,
  ReviewerInfo,
  ApprovalResponse,
} from './types';

declare const $: any;
declare const moment: any;

const ApprovalPanel = React.forwardRef<ApprovalPanelAPI, ApprovalPanelProps>(
  function ApprovalPanel(props, ref) {
    const {
      fileId,
      workflowName,
      canApprove,
      hasAlreadyReviewed,
      approveTitle,
      rejectTitle,
      userName,
      userId,
      history: initialHistory,
      remainingReviewers: initialReviewers,
      beforeApprovalChange,
      onApprovalChange,
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [history, setHistory] = useState<ApprovalHistoryItem[]>(initialHistory);
    const [reviewers, setReviewers] = useState<ReviewerInfo[]>(initialReviewers);
    const [reviewed, setReviewed] = useState(hasAlreadyReviewed);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    // Reset state when props change (file switch)
    useEffect(() => {
      setHistory(initialHistory);
      setReviewers(initialReviewers);
      setReviewed(hasAlreadyReviewed);
      setComment('');
      setSubmitting(false);
      setCollapsed(false);
    }, [fileId, initialHistory, initialReviewers, hasAlreadyReviewed]);

    useImperativeHandle(ref, () => ({
      getContainerElement: () => containerRef.current,
    }), []);

    const handleSubmit = useCallback((approved: boolean) => {
      if (submitting) return;

      // Allow callers to cancel the submission
      if (beforeApprovalChange && beforeApprovalChange(fileId, approved) === false) {
        return;
      }

      setSubmitting(true);

      const endpoint = approved ? 'ajax/approval.php?approve' : 'ajax/approval.php?reject';

      $.post(endpoint, { file_id: fileId, comment }, function (data: string) {
        let response: ApprovalResponse;
        try {
          response = JSON.parse(data);
        } catch {
          alert('Invalid response from server.');
          setSubmitting(false);
          return;
        }

        if (!response.success) {
          alert(response.error || 'Unknown error');
          setSubmitting(false);
          return;
        }

        const newWorkflowId = response.new_workflow_id;

        // Update workflow dropdown if workflow changed
        if (newWorkflowId != null) {
          const dropdown = document.getElementById('player-file-workflow-dropdown') as HTMLSelectElement | null;
          if (dropdown) dropdown.value = String(newWorkflowId);
        }

        // Optimistic UI: hide form, add to history, remove from reviewers
        setReviewed(true);

        const newEntry: ApprovalHistoryItem = {
          userId,
          userName,
          avatarUrl: `avatar?name=${encodeURIComponent(userName)}`,
          approved,
          comment,
          date: Date.now() / 1000,
        };

        setHistory(prev => [newEntry, ...prev]);
        setReviewers(prev => prev.filter(r => r.userId !== userId));
        setComment('');
        setSubmitting(false);

        // Notify caller of successful change
        if (onApprovalChange) {
          onApprovalChange(fileId, approved, newWorkflowId);
        }
      }).fail(function (_jqXHR: any, _textStatus: string, errorString: string) {
        alert('Failed to submit review: ' + errorString);
        setSubmitting(false);
      });
    }, [fileId, comment, submitting, userId, userName, beforeApprovalChange, onApprovalChange]);

    const formatRelativeDate = (timestamp: number, noFutureDates: boolean = true): string =>  {
      if (!timestamp) return '';

      let ms = timestamp * 1000;

      if (noFutureDates) {
        // Make sure no dates are in the future
        ms = Math.min(timestamp * 1000, Date.now() - 2000);
      }

      if (typeof moment !== 'undefined') {
          return moment(new Date(ms)).fromNow();
      }
      return new Date(ms).toLocaleString();
  };

    const handleApprove = useCallback(() => handleSubmit(true), [handleSubmit]);
    const handleReject = useCallback(() => handleSubmit(false), [handleSubmit]);
    const toggleCollapsed = useCallback(() => setCollapsed(prev => !prev), []);

    const showForm = workflowName && canApprove && !reviewed;
    const showPanel = showForm || history.length > 0 || reviewers.length > 0;

    if (!showPanel) return null;

    const reviewerList = reviewers.sort((a, b) => Number(b.isCurrentUser) - Number(a.isCurrentUser));

    return (
      <div id="approval-list" ref={containerRef} className={collapsed ? 'collapsed' : ''} role="presentation">
        {/* Approval form */}
        {showForm && (
          <div id="approval-container" className="pageseccolor" role="presentation">
            <button onClick={toggleCollapsed} style={{ border: 'none',
                padding: 0,
                margin: 0,
                maxWidth: 'initial',
                backgroundColor: 'transparent' }}
                aria-label="Toggle collapsed">
                <h3>{collapsed ? '▶' : '▼'} Approval</h3>
            </button>
            <div id="approval-button-area" role="presentation">
              <textarea
                id="approval_comment"
                placeholder="Approval comment (optional)"
                value={comment}
                onChange={e => setComment(e.target.value)}
                disabled={submitting}
                aria-label="Approval comment (optional)"
              />
              <button
                className="actionbtn approve"
                onClick={handleApprove}
                disabled={submitting}
                aria-label="Approve"
              >
                {approveTitle}
              </button>
              <button
                className="actionbtn reject"
                onClick={handleReject}
                disabled={submitting}
                aria-label="Reject"
              >
                {rejectTitle}
              </button>
            </div>
          </div>
        )}

        {/* History + remaining reviewers */}
        <div id="approval-history-container" role="list">
          {history.map((item, i) => (
            <div
              key={`${item.userId}-${i}`}
              className={`approval-history ${item.approved ? 'approved' : 'rejected'}`}
              data-user_id={item.userId}
              role="listitem"
            >
              <img src={item.avatarUrl} alt="" className="avatar" />
              <span className="avatar"></span>
              {item.userName} {item.approved ? 'approved' : 'rejected'}
              {item.comment && (
                <div className="approval-comment">{item.comment}</div>
              )}
              <div className="approval-date">{formatRelativeDate(item.date)}</div>
            </div>
          ))}

          {reviewerList.map(reviewer => (
            <div
              key={reviewer.userId}
              className="approval-history"
              data-user_id={reviewer.userId}
            >
              <img src={reviewer.avatarUrl} className="avatar" aria-hidden="true" />
              <span className="avatar">
                {reviewer.isCurrentUser
                  ? 'You need to review'
                  : `${reviewer.userName} needs to review`}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

export default ApprovalPanel;
